/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { useTextDimensions } from '../../hooks/PRIVATE_useTextDimensions';
import { AxisPosition, DataAxisProps, Dimension } from '../axis.types';
import { AXIS_DEFAULTS } from './axisDefaults';
const AXIS_MAX_RATIO = 0.3;
type GetTextDimensions = NonNullable<ReturnType<typeof useTextDimensions>['getTextDimensions']>;

/**
 * Returns resolved axis position.
 * @param axis
 * @param orientation
 * @param isRtl
 */
export function getAxesPosition(
  yAxis: DataAxisProps | undefined,
  isHoriz: boolean,
  isRtl: boolean
): {
  xAxisPosition: AxisPosition;
  yAxisPosition: AxisPosition;
} {
  let xAxisPosition, yAxisPosition: AxisPosition;

  if (isHoriz) {
    yAxisPosition = yAxis?.position === 'top' ? 'top' : 'bottom';
    xAxisPosition = (isRtl ? 'right' : 'left') as AxisPosition;
  } else {
    xAxisPosition = 'bottom' as AxisPosition;
    const isYEnd = yAxis?.position === 'end';
    yAxisPosition = isYEnd ? (isRtl ? 'left' : 'right') : isRtl ? 'right' : 'left';
  }
  return { xAxisPosition, yAxisPosition };
}

/**
 * Returns the scale for the axis.
 * @param availSpace
 * @param axisViewportMin
 * @param axisViewportMax
 * @param position
 * @returns
 */
export function getAxisRange(
  availSpace: Dimension,
  position: 'left' | 'right' | 'top' | 'bottom',
  isRtl: boolean
) {
  const { x, y, width, height } = availSpace;
  const y2 = y + height;
  const x2 = x + width;

  let rangeStart: number;
  let rangeEnd: number;

  if (position === 'left' || position === 'right') {
    rangeStart = y2;
    rangeEnd = y;
  } else {
    rangeStart = isRtl ? x2 : x;
    rangeEnd = isRtl ? x : x2;
  }

  return [rangeStart, rangeEnd];
}

/**
 * Returns the maximum possible size of the axis.
 * @param availSpace The available space.
 * @param isHoriz If the axis is horizontal.
 * @param axisSize The ratio of axis to the chart.
 * @returns
 */
export function getAxisMaxSize(availSpace: Dimension, isHoriz: boolean, axisSize?: number) {
  const size = Math.max(0, Math.min(1, axisSize != undefined ? axisSize : AXIS_MAX_RATIO));
  return (isHoriz ? availSpace.height : availSpace.width) * size;
}

/**
 * Returns the width of a group for given availableSpace, axisposition and axis extents.
 * @param availSpace The available space to render the axis
 * @param position The position of the axis
 * @param min The minimum value of axis coord
 * @param max The maximum value of axis coord
 * @returns
 */
export function getGroupWidth(
  availSpace: { x: number; y: number; width: number; height: number },
  position: 'left' | 'right' | 'top' | 'bottom',
  min: number,
  max: number
) {
  const isHoriz = position === 'top' || position === 'bottom';
  if (isHoriz) {
    return availSpace.width / (max - min);
  } else {
    return availSpace.height / (max - min);
  }
}

/**
 * Returns the num of labels to skip safely.
 * @param groupWidth
 * @param tickLabelStyle
 * @param getTextDimensions
 * @returns
 */
export function getSafeSkips(
  groupWidth: number,
  tickLabelStyle: Partial<CSSStyleDeclaration>,
  getTextDimensions?: GetTextDimensions
) {
  if (!getTextDimensions) {
    return 0;
  }
  const textDims = getTextDimensions('MW', tickLabelStyle as any);
  const gaps = AXIS_DEFAULTS.labelGap;
  return Math.floor((0.5 * textDims.width + gaps) / (2 * groupWidth));
}
